import xaccelClient from "@/utils/xaccel-client";
import {XaccelStreamMetadata, StreamStats} from "@/model/models";
import {cachedInSession, invalidateSessionCache, xaccelStreamIdsKey} from "@/utils/session-cache";

class XaccelService {

    public getBackendHealth() {
        return new Promise((resolve, reject) => {
            xaccelClient
                .get('/health')
                .then(res => resolve(res.data))
                .catch(reject)
        })
    }

    public startXaccelStream( providerName: string, streamIdentifier: number ) {
        return xaccelClient
            .post('/start-stream', { stream_identifier: streamIdentifier }, { params: { providerName } })
            .then(res => res.data)
    }

    public stopXaccelStream( providerName: string, streamIdentifier: number ) {
        return xaccelClient
            .post('/stop-stream', { stream_identifier: streamIdentifier }, { params: { providerName } })
            .then(res => res.data)
    }


    public getXaccelStreamMetadata(providerName: string, streamIdentifier: number) {
        return new Promise<XaccelStreamMetadata>((resolve, reject) => {
            xaccelClient
                .post('/get-stream-metadata', { stream_identifier: streamIdentifier }, { params: { providerName } })
                .then(res => resolve(res.data))
                .catch(reject)
        })
    }public getStreamStatus(providerName: string, streamIdentifier: number) {
        return new Promise<XaccelStreamMetadata>((resolve, reject) => {
            xaccelClient
                .post('/get-stream-status', { stream_identifier: streamIdentifier }, { params: { providerName } })
                .then(res => resolve(res.data.status))
                .catch(reject)
        })
    }
    // cache=true serves/stores a per-encoder sessionStorage copy (the calendar source editors,
    // which call this on every Edit/Set up open). The default stays live, so EventBroadcast
    // still sees a fresh list. The copy is dropped by updateXaccelConfig for that encoder.
    public getXaccelStreamIds(providerName: string, cache = false): Promise<XaccelStreamMetadata> {
        return cache
            ? cachedInSession(xaccelStreamIdsKey(providerName), () => this.fetchXaccelStreamIds(providerName))
            : this.fetchXaccelStreamIds(providerName);
    }

    private fetchXaccelStreamIds(providerName: string) {
        return new Promise<XaccelStreamMetadata>((resolve, reject) => {
            xaccelClient
                .get('/get-stream-ids', { params: { providerName } })
                .then(res => resolve(res.data))
                .catch(reject)
        })
    }

    public getIPTVData(encoderName: string) {
        return xaccelClient.get(
            '/get-input-sources-from-m3u',
            { params: { providerName: encoderName } }
        );
    }

    // Encoder hardcoded-data (xaccel) doesn't change per visit, so it's cached in
    // sessionStorage under one key and fetched once per session, same pattern as
    // AdminService.getCalendarStaticData() - resolves with the parsed data directly rather
    // than requiring every caller (CalMatches/MatchEvents/Daily/EventBroadcast) to also read
    // sessionStorage itself. Previously this always hit the network and only wrote the cache
    // afterward, which meant Daily's per-day remount (App.vue keys <router-view> on the full
    // path, so Prev/Next/Today remounts the page) refetched it on every single navigation.
    public getHardcodedData() {
        return new Promise((resolve, reject) => {
            const stored = sessionStorage.getItem("hardcodedData");
            if (stored) {
                resolve(JSON.parse(stored));
                return;
            }
            xaccelClient
                .get("/hardcoded-data", { params: { providerName: "test" } })
                .then((res) => {
                    const data = res?.data ?? {};
                    sessionStorage.setItem("hardcodedData", JSON.stringify(data));
                    resolve(data);
                })
                .catch(reject);
        });
    }

    public getAllStreamStats(providerName: string): Promise<StreamStats[]> {
        return xaccelClient
            .get('/get-all-stream-stats', { params: { providerName } })
            .then(res => res.data.streams || []);
    }

    public updateXaccelConfig(encoderName: string, body: any) {
        return xaccelClient.post(
            '/update-config',
            body,
            { params: { providerName: encoderName } }
        ).then((res) => {
            // This encoder's stream list may have changed - drop the calendar editors' copy.
            invalidateSessionCache(xaccelStreamIdsKey(encoderName));
            return res;
        });
    }
}
export default new XaccelService ()